import { Reseller } from '@/modules/Reseller/types'
import {
    getResellerStatusOptions,
    getResellerTierOptions,
    RESELLER_STATUS_OPTIONS,
    RESELLER_TIER_OPTIONS,
} from '@/modules/Reseller/constants'
import { useTranslation } from 'react-i18next'
import { GridInformationWithHookForm } from 'vnetwork-ui'
import CustomerIcons from '@/icons/CustomerIcons'
import SettingIcons from '@/icons/SettingIcons'
import dayjs from 'dayjs'
import { updateReseller } from '@/modules/Reseller/services'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import * as yup from 'yup'
import Paper from '@/components/Paper'
import CellStatus, { Color } from '@/components/GokuDataGrid/CellStatus'

interface OverviewProps {
    reseller?: Reseller
    refetch?: () => void
}

const updateResellerSchema = yup.object({
    reseller_name: yup.string().required('reseller_name is required'),
    reseller_tier: yup.string(),
    reseller_status: yup.string(),
})

const Overview: React.FC<OverviewProps> = ({ reseller, refetch }) => {
    const { t } = useTranslation('reseller')

    const handleEdit = async (values: any) => {
        if (!reseller) return
        return updateReseller({
            reseller_id: reseller.reseller_id,
            payload: values,
        })
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: handleEdit },
        {
            reactQueryKeys: QUERY_KEYS.RESELLERS,
            successMessage: t('updated-success'),
            errorMessage: t('Something went wrong'),
            onSuccess: () => {
                refetch?.()
            },
        }
    )

    if (!reseller) return null

    const tierOption = RESELLER_TIER_OPTIONS(t)?.find(
        (item) => item?.value === reseller.reseller_tier
    )
    const statusOption = RESELLER_STATUS_OPTIONS(t)?.find(
        (item) => item?.value === reseller.reseller_status
    )

    return (
        <div className="space-y-4">
            {/* Basic Information */}
            <GridInformationWithHookForm
                loading={isPending}
                onEdit={mutateAsync}
                schema={updateResellerSchema as any}
                data={[
                    {
                        label: t('reseller_id'),
                        value: reseller.reseller_id,
                    },
                    {
                        label: t('reseller_name'),
                        value: reseller.reseller_name,
                        formItemProps: {
                            name: 'reseller_name',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('tier'),
                        value: reseller.reseller_tier ? (
                            <CellStatus
                                label={
                                    tierOption?.label || reseller.reseller_tier
                                }
                                color={tierOption?.color as Color}
                                className="capitalize"
                            />
                        ) : (
                            '-'
                        ),
                        formItemProps: {
                            name: 'reseller_tier',
                            type: 'select',
                            options: getResellerTierOptions(t),
                        },
                    },
                    {
                        label: t('status'),
                        value: (
                            <CellStatus
                                label={
                                    statusOption?.label ||
                                    reseller.reseller_status ||
                                    t('active')
                                }
                                color={statusOption?.color as Color}
                                className="capitalize"
                            />
                        ),
                        formItemProps: {
                            name: 'reseller_status',
                            type: 'select',
                            options: getResellerStatusOptions(t),
                        },
                    },
                    {
                        label: t('organizations'),
                        value: reseller.organization_count || 0,
                    },
                    {
                        label: t('created_at'),
                        value: reseller.created_at
                            ? dayjs(reseller.created_at).format(
                                  'DD/MM/YYYY HH:mm:ss'
                              )
                            : '-',
                    },
                    {
                        label: t('updated_at'),
                        value: reseller.updated_at
                            ? dayjs(reseller.updated_at).format(
                                  'DD/MM/YYYY HH:mm:ss'
                              )
                            : '-',
                    },
                ]}
                className="mt-4"
                head={{
                    title: t('basic_information'),
                    icon: <SettingIcons />,
                }}
                defaultFormValues={reseller}
            />

            {/* Customer Account */}
            {reseller.customer && (
                <Paper className="mt-4">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-6 text-primary">
                                <CustomerIcons />
                            </div>
                            <span className="font-semibold text-bg-primary text-sm">
                                {t('customer_account')}
                            </span>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <p className="text-sm text-gray-500">
                                {t('customer_id')}
                            </p>
                            <p className="text-sm mt-2">
                                {reseller.customer.customer_id}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {t('customer_name')}
                            </p>
                            <p className="text-sm mt-2">
                                {reseller.customer.customer_name}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {t('email')}
                            </p>
                            <p className="text-sm mt-2">
                                {reseller.customer.customer_email}
                            </p>
                        </div>
                    </div>
                </Paper>
            )}
        </div>
    )
}

export default Overview
